import { useCallback, useEffect, useRef, useState } from 'react';

/** Total inactivity allowed before the session is terminated (60 minutes). */
export const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;

/** How long before timeout the warning dialog appears (5 minutes → warns at 55). */
export const INACTIVITY_WARNING_BEFORE_MS = 5 * 60 * 1000;

/** How often the elapsed inactivity is evaluated. */
export const INACTIVITY_CHECK_INTERVAL_MS = 15 * 1000;

/** Minimum gap between activity-timestamp writes (avoids a write per mousemove). */
export const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;

/** localStorage key shared across tabs so activity in any tab keeps the session alive. */
export const LAST_ACTIVITY_STORAGE_KEY = 'pp:lastActivityAt';

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
];

interface UseInactivityTimeoutOptions {
  /** Only track activity / enforce timeout while true (i.e. while authenticated). */
  enabled: boolean;
  /** Called once when the inactivity timeout is reached. */
  onTimeout: () => void;
}

interface UseInactivityTimeoutResult {
  /** True when the pre-timeout warning should be shown. */
  showWarning: boolean;
  /** Epoch ms at which the session times out (for countdown display). */
  timeoutAt: number | null;
  /** Resets the inactivity clock and dismisses the warning ("Stay signed in"). */
  staySignedIn: () => void;
}

function readLastActivity(): number | null {
  const raw = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Tracks user activity (mouse, keyboard, touch, scroll) and invokes `onTimeout`
 * after 60 minutes of inactivity. A warning is exposed 5 minutes before timeout.
 *
 * The last-activity timestamp lives in localStorage so activity in any tab of the
 * portal keeps every tab's session alive, and a backgrounded/slept tab still times
 * out correctly on wake (checked on `visibilitychange` as well as on an interval).
 *
 * While the warning is showing, passive activity (e.g. nudging the mouse) does NOT
 * reset the clock — only the explicit `staySignedIn` action does. This prevents the
 * warning from being dismissed accidentally.
 */
export function useInactivityTimeout({
  enabled,
  onTimeout,
}: UseInactivityTimeoutOptions): UseInactivityTimeoutResult {
  const [showWarning, setShowWarning] = useState(false);
  const [timeoutAt, setTimeoutAt] = useState<number | null>(null);
  const lastWriteRef = useRef(0);
  const timedOutRef = useRef(false);
  const showWarningRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  showWarningRef.current = showWarning;

  const recordActivity = useCallback((force = false) => {
    const now = Date.now();
    if (!force && showWarningRef.current) return; // passive activity can't dismiss the warning
    if (!force && now - lastWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) return;
    lastWriteRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(now));
  }, []);

  const staySignedIn = useCallback(() => {
    recordActivity(true);
    setShowWarning(false);
    setTimeoutAt(null);
  }, [recordActivity]);

  useEffect(() => {
    if (!enabled) {
      setShowWarning(false);
      setTimeoutAt(null);
      timedOutRef.current = false;
      return;
    }

    timedOutRef.current = false;
    recordActivity(true); // session starts/resumes as "active now"

    const handleActivity = (): void => recordActivity();

    const check = (): void => {
      if (timedOutRef.current) return;
      const lastActivity = readLastActivity() ?? Date.now();
      const elapsed = Date.now() - lastActivity;

      if (elapsed >= INACTIVITY_TIMEOUT_MS) {
        timedOutRef.current = true;
        setShowWarning(false);
        setTimeoutAt(null);
        localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
        onTimeoutRef.current();
      } else if (elapsed >= INACTIVITY_TIMEOUT_MS - INACTIVITY_WARNING_BEFORE_MS) {
        setShowWarning(true);
        setTimeoutAt(lastActivity + INACTIVITY_TIMEOUT_MS);
      } else {
        // Activity in another tab may have cleared the warning condition
        setShowWarning(false);
        setTimeoutAt(null);
      }
    };

    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') check();
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, handleActivity));
    document.addEventListener('visibilitychange', handleVisibility);
    const interval = setInterval(check, INACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, [enabled, recordActivity]);

  return { showWarning, timeoutAt, staySignedIn };
}
